import axios from 'axios';
import { getAuthToken } from '../utils/authUtils';

// Get API base URL
const getApiBaseUrl = () => {
  return process.env.REACT_APP_FIREBASE_BACKEND_API_BASE || 'https://quantumleap-emr-dev.web.app';
};

/**
 * Get all billing periods for a facility
 * @param {string} facilityId - The facility ID
 * @param {number} limit - Maximum number of periods to return (default 24)
 * @returns {Promise<Array>} - Array of billing periods
 */
export const getAllPeriods = async (facilityId, limit = 24) => {
  try {
    const token = await getAuthToken();
    const response = await axios.get(
      `${getApiBaseUrl()}/month-end-close/periods`,
      {
        params: { facilityId, limit },
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    return response.data.periods || [];
  } catch (error) {
    console.error('Error fetching billing periods:', error);
    throw error;
  }
};

/**
 * Get current billing period
 * @param {string} facilityId - The facility ID
 * @returns {Promise<Object>} - Current billing period
 */
export const getCurrentPeriod = async (facilityId) => {
  try {
    const token = await getAuthToken();
    const response = await axios.get(
      `${getApiBaseUrl()}/month-end-close/periods/current`,
      {
        params: { facilityId },
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    return response.data.period;
  } catch (error) {
    console.error('Error fetching current period:', error);
    throw error;
  }
};

/**
 * Get specific period status
 * @param {string} facilityId - The facility ID
 * @param {number} year - Year
 * @param {number} month - Month (1-12)
 * @returns {Promise<Object>} - Period status
 */
export const getPeriodStatus = async (facilityId, year, month) => {
  try {
    const token = await getAuthToken();
    const response = await axios.get(
      `${getApiBaseUrl()}/month-end-close/periods/${year}/${month}`,
      {
        params: { facilityId },
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    return response.data.period;
  } catch (error) {
    console.error('Error fetching period status:', error);
    throw error;
  }
};

/**
 * Initiate soft close for a period
 * @param {string} facilityId - The facility ID
 * @param {number} year - Year
 * @param {number} month - Month (1-12)
 * @returns {Promise<Object>} - Updated period
 */
export const initiateSoftClose = async (facilityId, year, month) => {
  try {
    const token = await getAuthToken();
    const response = await axios.post(
      `${getApiBaseUrl()}/month-end-close/periods/${year}/${month}/soft-close`,
      { facilityId },
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error initiating soft close:', error);
    throw error;
  }
};

/**
 * Initiate hard close for a period
 * @param {string} facilityId - The facility ID
 * @param {number} year - Year
 * @param {number} month - Month (1-12)
 * @returns {Promise<Object>} - Updated period
 */
export const initiateHardClose = async (facilityId, year, month) => {
  try {
    const token = await getAuthToken();
    const response = await axios.post(
      `${getApiBaseUrl()}/month-end-close/periods/${year}/${month}/hard-close`,
      { facilityId },
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error initiating hard close:', error);
    throw error;
  }
};

/**
 * Reopen a closed period
 * @param {string} facilityId - The facility ID
 * @param {number} year - Year
 * @param {number} month - Month (1-12)
 * @param {string} reason - Reason for reopening
 * @returns {Promise<Object>} - Updated period
 */
export const reopenPeriod = async (facilityId, year, month, reason) => {
  try {
    const token = await getAuthToken();
    const response = await axios.post(
      `${getApiBaseUrl()}/month-end-close/periods/${year}/${month}/reopen`,
      { facilityId, reason },
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error reopening period:', error);
    throw error;
  }
};

/**
 * Validate if a transaction date is allowed
 * @param {string} facilityId - The facility ID
 * @param {Date|string} transactionDate - The date to validate
 * @param {string} transactionType - Type of transaction: 'create', 'edit', 'delete', 'payment'
 * @returns {Promise<Object>} - Validation result
 */
export const validateTransactionDate = async (facilityId, transactionDate, transactionType = 'edit') => {
  try {
    const token = await getAuthToken();
    const dateStr = transactionDate instanceof Date 
      ? transactionDate.toISOString() 
      : transactionDate;
    
    const response = await axios.get(
      `${getApiBaseUrl()}/month-end-close/validate/${encodeURIComponent(dateStr)}`,
      {
        params: { facilityId, type: transactionType },
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error validating transaction date:', error);
    throw error;
  }
};

/**
 * Refresh period summary
 * @param {string} facilityId - The facility ID
 * @param {number} year - Year
 * @param {number} month - Month (1-12)
 * @returns {Promise<Object>} - Updated summary
 */
export const refreshPeriodSummary = async (facilityId, year, month) => {
  try {
    const token = await getAuthToken();
    const response = await axios.post(
      `${getApiBaseUrl()}/month-end-close/periods/${year}/${month}/refresh-summary`,
      { facilityId },
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error refreshing period summary:', error);
    throw error;
  }
};

/**
 * Get facility billing configuration
 * @param {string} facilityId - The facility ID
 * @returns {Promise<Object>} - Billing configuration
 */
export const getBillingConfig = async (facilityId) => {
  try {
    const token = await getAuthToken();
    const response = await axios.get(
      `${getApiBaseUrl()}/month-end-close/config`,
      {
        params: { facilityId },
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    return response.data.config;
  } catch (error) {
    console.error('Error fetching billing config:', error);
    throw error;
  }
};

/**
 * Update facility billing configuration
 * @param {string} facilityId - The facility ID
 * @param {Object} config - Configuration to update
 * @param {string} config.accountingMethod - 'accrual' or 'cash'
 * @param {number} config.autoHardCloseGracePeriodDays - Days before auto hard close
 * @param {boolean} config.monthEndCloseEnabled - Enable/disable feature
 * @returns {Promise<Object>} - Updated configuration
 */
export const updateBillingConfig = async (facilityId, config) => {
  try {
    const token = await getAuthToken();
    const response = await axios.put(
      `${getApiBaseUrl()}/month-end-close/config`,
      { facilityId, ...config },
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error updating billing config:', error);
    throw error;
  }
};

/**
 * Get period display name
 * @param {number} year - Year
 * @param {number} month - Month (1-12)
 * @returns {string} - Display name like "December 2024"
 */
export const getPeriodDisplayName = (year, month) => {
  const date = new Date(year, month - 1, 1);
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
};

/**
 * Get status color for UI
 * @param {string} status - Period status
 * @returns {string} - MUI color name
 */
export const getStatusColor = (status) => {
  switch (status) {
    case 'open':
      return 'success';
    case 'soft_closed':
      return 'warning';
    case 'hard_closed':
      return 'error';
    default:
      return 'default';
  }
};

/**
 * Get status label for UI
 * @param {string} status - Period status
 * @returns {string} - Human-readable label
 */
export const getStatusLabel = (status) => {
  switch (status) {
    case 'open':
      return 'Open';
    case 'soft_closed':
      return 'Soft Closed';
    case 'hard_closed':
      return 'Closed';
    default:
      return 'Unknown';
  }
};

/**
 * Format currency
 * @param {number} amount - Amount to format
 * @returns {string} - Formatted currency string
 */
export const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(amount || 0);
};

// ============================================
// Snapshot Functions
// ============================================

/**
 * Get all snapshots for a facility
 * @param {string} facilityId - The facility ID
 * @param {number} limit - Maximum number of snapshots to return
 * @returns {Promise<Array>} - Array of snapshots
 */
export const getSnapshots = async (facilityId, limit = 24) => {
  try {
    const token = await getAuthToken();
    const response = await axios.get(
      `${getApiBaseUrl()}/month-end-close/snapshots`,
      {
        params: { facilityId, limit },
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    return response.data.snapshots || [];
  } catch (error) {
    console.error('Error fetching snapshots:', error);
    throw error;
  }
};

/**
 * Get a specific snapshot by ID
 * @param {string} snapshotId - The snapshot ID
 * @returns {Promise<Object>} - Snapshot details
 */
export const getSnapshotById = async (snapshotId) => {
  try {
    const token = await getAuthToken();
    const response = await axios.get(
      `${getApiBaseUrl()}/month-end-close/snapshots/${snapshotId}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    return response.data.snapshot;
  } catch (error) {
    console.error('Error fetching snapshot:', error);
    throw error;
  }
};

/**
 * Get bill details for a snapshot
 * @param {string} snapshotId - The snapshot ID
 * @param {number} limit - Maximum number of bills to return
 * @param {number} offset - Offset for pagination
 * @returns {Promise<Object>} - Bills and pagination info
 */
export const getSnapshotBills = async (snapshotId, limit = 100, offset = 0) => {
  try {
    const token = await getAuthToken();
    const response = await axios.get(
      `${getApiBaseUrl()}/month-end-close/snapshots/${snapshotId}/bills`,
      {
        params: { limit, offset },
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    return {
      bills: response.data.bills || [],
      total: response.data.total || 0,
      limit: response.data.limit,
      offset: response.data.offset
    };
  } catch (error) {
    console.error('Error fetching snapshot bills:', error);
    throw error;
  }
};

/**
 * Get snapshot for a specific period
 * @param {string} facilityId - The facility ID
 * @param {number} year - Year
 * @param {number} month - Month (1-12)
 * @returns {Promise<Object|null>} - Snapshot or null
 */
export const getSnapshotForPeriod = async (facilityId, year, month) => {
  try {
    const token = await getAuthToken();
    const response = await axios.get(
      `${getApiBaseUrl()}/month-end-close/snapshots/period/${facilityId}/${year}/${month}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    return response.data.snapshot;
  } catch (error) {
    if (error.response?.status === 404) {
      return null;
    }
    console.error('Error fetching period snapshot:', error);
    throw error;
  }
};

/**
 * Verify snapshot integrity
 * @param {string} snapshotId - The snapshot ID
 * @returns {Promise<Object>} - Verification result
 */
export const verifySnapshotIntegrity = async (snapshotId) => {
  try {
    const token = await getAuthToken();
    const response = await axios.get(
      `${getApiBaseUrl()}/month-end-close/snapshots/${snapshotId}/verify`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    return response.data.verification;
  } catch (error) {
    console.error('Error verifying snapshot:', error);
    throw error;
  }
};

/**
 * Create a snapshot for a hard-closed period that doesn't have one
 * @param {string} facilityId - The facility ID
 * @param {number} year - The year
 * @param {number} month - The month (1-12)
 * @returns {Promise<Object>} - Result with snapshotId
 */
export const createSnapshotForPeriod = async (facilityId, year, month) => {
  try {
    const token = await getAuthToken();
    const response = await axios.post(
      `${getApiBaseUrl()}/month-end-close/periods/${year}/${month}/create-snapshot?facilityId=${facilityId}`,
      {},
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error creating snapshot for period:', error);
    throw error;
  }
};

const monthEndCloseService = {
  getAllPeriods,
  getCurrentPeriod,
  getPeriodStatus,
  initiateSoftClose,
  initiateHardClose,
  reopenPeriod,
  validateTransactionDate,
  refreshPeriodSummary,
  getBillingConfig,
  updateBillingConfig,
  getPeriodDisplayName,
  getStatusColor,
  getStatusLabel,
  formatCurrency,
  // Snapshot functions
  getSnapshots,
  getSnapshotById,
  getSnapshotBills,
  getSnapshotForPeriod,
  verifySnapshotIntegrity,
  createSnapshotForPeriod
};

export default monthEndCloseService;

